// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import IntlMessageFormat from 'intl-messageformat';
import api from './api';
import Observer from './Observer';
import BaseTranslate, { TranslateProps } from 'components/common/Translate';

type Messages = Record<string, string>;
type Sections = Record<string, Locale>;

class Locale {
    static sections: Sections = {};

    /**
     * Register new locale section
     */
    static addSection(name: string, messages?: Messages): Locale {
        this.sections[name] = new Locale(messages);
        Observer.notify({ name, messages }, 'plesk:addedLocaleSection');
        return this.sections[name];
    }

    /**
     * Register new locale sections
     */
    static addSections(sections: Record<string, Messages>): Sections {
        Object.entries(sections).forEach(([name, messages]) => {
            this.sections[name] = new Locale(messages);
        });
        Observer.notify({ sections }, 'plesk:addedLocaleSections');
        return this.sections;
    }

    /**
     * Returns registered locale section or created empty section
     */
    static getSection(name: string): Locale {
        if (!this.sections[name]) {
            return new Locale();
        }
        return this.sections[name];
    }

    static getTranslate(name: string) {
        const Translate = (props: TranslateProps) => <BaseTranslate namespace={name} {...props} />;
        return Translate;
    }

    /**
     * Load, register, and returns locale sections
     */
    static async loadSections(names: string[], locale?: string): Promise<Locale[]> {
        const namesToLoad = names.filter(name => locale || !this.sections[name]);

        if (namesToLoad.length) {
            const params = namesToLoad.map(section => `sections[]=${encodeURIComponent(section)}`);
            if (locale) {
                params.push(`locale=${encodeURIComponent(locale)}`);
            }
            try {
                const sections = await api.get(`/cp/locale?${params.join('&')}`) as Record<string, Messages>;
                this.addSections(sections);
            } catch { }
        }

        return names.map(name => this.getSection(name));
    }

    /**
     * Load, register, and returns locale section
     */
    static async loadSection(name: string): Promise<Locale> {
        const [locale] = await this.loadSections([name]);
        return locale;
    }

    messages: Messages;

    constructor(messages: Messages = {}) {
        this.messages = messages;
    }

    /**
     * Get locale message by key.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    lmsg(key: string, params?: Record<string, any>): string {
        const value = this.messages[key];
        if (typeof value === 'undefined') {
            return `[${key}]`;
        }

        if (typeof params === 'undefined') {
            return value;
        }

        return Object.keys(params)
            .reduce((val, paramKey) => val.replace(`%%${paramKey}%%`, params[paramKey]), value);
    }

    /**
     * Get formatted locale message by key.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formatMessage(key: string, params?: Record<string, any>) {
        return new IntlMessageFormat(this.lmsg(key, params), document.documentElement.lang || 'en-US').format<string>(params);
    }
}

export default Locale;
